import { observer } from 'mobx-react-lite'
import React, {
  forwardRef,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { BreakoutTooltip } from '../../components/design-system/BreakoutTooltip'
import classNames from 'classnames'
import { useNavigationCubit } from 'hooks/cubits/navigation'
import { useSettings } from 'hooks/settings'
import { useRootStore } from 'hooks/rootStore'
import { getRouteName, routes } from 'config/routes'

// --- NavItem: styled nav element (button or anchor) ---

type NavItemIcon = React.ComponentType<{
  size: number
  className?: string
  strokeWidth?: number
}>

type NavCommonProps = {
  Icon: NavItemIcon
  label: string
  alwaysShowLabel?: boolean
  // Rendered after the label in the expanded nav, e.g. a pending-count chip.
  badge?: React.ReactNode
  'data-testid'?: string
}

type NavItemProps = NavCommonProps & {
  forceDisableTooltip?: boolean
  active?: boolean
  href?: string
  target?: string
  rel?: string
  onClick?: (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void
} & Pick<
    React.HTMLProps<HTMLElement>,
    'tabIndex' | 'aria-expanded' | 'aria-controls' | 'aria-haspopup' | 'id'
  >

export const NavItem = observer(
  // eslint-disable-next-line mobx/no-anonymous-observer
  forwardRef<HTMLElement, NavItemProps>(function NavItem(
    {
      Icon,
      label,
      badge,
      active = false,
      alwaysShowLabel,
      href,
      target,
      rel,
      onClick,
      onKeyUp,
      onKeyDown,
      forceDisableTooltip = false,
      ...rest
    },
    ref
  ) {
    const baseClassName = `w-full text-on-surface-var stroke-on-surface-var cursor-pointer rounded-lg group hover:bg-surface-bright focus:bg-surface-bright focus-ring-double`
    const activeClassName = `bg-surface-bright stroke-on-surface! text-on-surface!`

    const [isTruncated, setIsTruncated] = useState(false)
    const { isMobile, isOpen, animationDuration } = useNavigationCubit()
    const { animationsEnabled } = useSettings()

    // if isOpen changes in desktop view with animations enabled
    // do NOT re-render the component w/ or w/o label
    // if this updates during the component lifecycle it is on the way out
    // and a new one will render in its place
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const isOpenOnMount = useMemo(() => isOpen, [])
    const isOpenToUse = animationsEnabled && !isMobile ? isOpenOnMount : isOpen

    const labelRef = useRef<HTMLElement>(null)

    // if label is truncated then show tooltip
    // todo: maybe add window resize listener?
    useLayoutEffect(() => {
      // check for truncation after animation finished
      const timeoutId = setTimeout(
        () => {
          if (!labelRef.current) return
          setIsTruncated(
            labelRef.current.scrollWidth > labelRef.current.clientWidth
          )
        },
        animationsEnabled ? animationDuration : 0
      )
      return () => clearTimeout(timeoutId)
    }, [label, isOpen, animationsEnabled, animationDuration])

    const hasIconStrokeProps = active
    const iconStrokeProps = active ? { strokeWidth: 2 } : {}

    const isLink = Boolean(href)

    // Compute mode-specific className and content
    let elementClassName: string
    let content: React.ReactNode
    let tooltipEnabled = false
    let tooltipContent = ''

    if (isMobile) {
      elementClassName = classNames(
        'group inline-block w-full cursor-pointer truncate px-4 py-6 hover:bg-surface',
        { 'bg-surface': active }
      )
      content = (
        <>
          <Icon
            size={25}
            className={classNames('mr-3 inline', {
              'stroke-[1.6]': !hasIconStrokeProps,
            })}
            {...iconStrokeProps}
          />
          {label}
          {badge && <span className="ml-2 align-middle">{badge}</span>}
        </>
      )
    } else if (!isOpenToUse && !alwaysShowLabel) {
      // Collapsed desktop
      elementClassName = classNames(
        baseClassName,
        'inline-block py-2.5 text-center leading-none',
        { [activeClassName]: active }
      )
      content = (
        <div className="relative inline-block h-5 text-center align-middle">
          <Icon
            size={20}
            className={classNames({ 'stroke-[1.6]': !hasIconStrokeProps })}
            {...iconStrokeProps}
          />
          {/* Collapsed: a badge can't sit after a hidden label, so overlay it
              on the icon's top-right corner, like the notifications bell. */}
          {badge && (
            <span className="absolute -top-2 -right-2.5 leading-none">
              {badge}
            </span>
          )}
        </div>
      )
      tooltipEnabled = Boolean(label)
      tooltipContent = label || ''
    } else {
      // Expanded desktop
      elementClassName = classNames(
        baseClassName,
        'flex flex-row items-center gap-2 py-2.5',
        {
          [activeClassName]: active,
          'px-3 text-left': label,
          'justify-center': !label,
        }
      )
      content = (
        <>
          <div className="inline-block h-5 text-center align-middle">
            <Icon
              size={20}
              className={classNames({ 'stroke-[1.6]': !hasIconStrokeProps })}
              {...iconStrokeProps}
            />
          </div>
          {label && (
            <strong
              ref={labelRef}
              className={classNames('truncate', {
                'text-body-medium': !active,
                'text-label-medium': active,
              })}
            >
              {label}
            </strong>
          )}
          {badge && <div className="ml-auto">{badge}</div>}
        </>
      )
      tooltipEnabled = Boolean(label) && isTruncated
      tooltipContent = label || ''
    }

    const element = isLink ? (
      <a
        ref={(node) => {
          if (ref && typeof ref === 'function') {
            ref(node)
          } else if (ref && 'current' in ref) {
            ref.current = node
          }
        }}
        aria-label={label ?? undefined}
        aria-current={active ? 'page' : undefined}
        href={href}
        target={target}
        rel={rel}
        onClick={onClick}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        className={elementClassName}
        {...rest}
      >
        {content}
      </a>
    ) : (
      <button
        ref={(node) => {
          if (ref && typeof ref === 'function') {
            ref(node)
          } else if (ref && 'current' in ref) {
            ref.current = node
          }
        }}
        aria-label={label ?? undefined}
        type="button"
        onClick={onClick}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        className={elementClassName}
        {...rest}
      >
        {content}
      </button>
    )

    if (!isMobile && !forceDisableTooltip) {
      return (
        <BreakoutTooltip
          ariaHidden
          enabled={tooltipEnabled}
          content={tooltipContent}
          placement="right"
        >
          {element}
        </BreakoutTooltip>
      )
    }

    return element
  })
)

// --- NavLink: routing wrapper around NavItem ---
type NavLinkProps = NavCommonProps & {
  href: string
  target?: string
}

// --- List item wrappers ---
export function NavLinkLi({
  href,
  alwaysShowLabel,
  Icon,
  label,
  target,
  'data-testid': testId,
}: NavLinkProps) {
  return (
    <li>
      <NavItem
        href={href}
        rel={target === '_blank' ? 'noopener noreferrer' : undefined}
        alwaysShowLabel={alwaysShowLabel}
        Icon={Icon}
        label={label}
        target={target}
        data-testid={testId}
      />
    </li>
  )
}

type NavRouteProps = NavCommonProps & {
  to: string
  activeOn?: string
  onActivate?: () => void
  // Fired just before navigating, e.g. to close a popover that owns this item.
  onNavigate?: () => void
}
export const NavRouteLi = observer(function NavRouteLi(props: NavRouteProps) {
  const { to, activeOn, onActivate, onNavigate, ...rest } = props
  const store = useRootStore()
  const route = to ? routes[to] : null
  const path = route?.path

  const activeOnArray = useMemo(() => {
    if (activeOn) return activeOn.split(',').map((s) => s.trim())
    return [to]
  }, [activeOn, to])

  const isActive = useMemo(() => {
    const routeName = getRouteName(store.router.currentRoute)
    return Boolean(routeName && activeOnArray.includes(routeName))
  }, [store.router.currentRoute, activeOnArray])

  const handleOnClick = useCallback(
    (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      onActivate?.()
      // Let the browser handle modified clicks (cmd/ctrl/shift/alt + click)
      // natively so the anchor's href opens a new tab/window instead of
      // SPA-navigating in place. `'button' in e` limits this to mouse clicks so
      // keyboard activation still navigates. Middle/right click never fire
      // onClick (auxclick / context menu), so there's nothing to guard there.
      if ('button' in e && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) {
        return
      }
      e.preventDefault()
      onNavigate?.()
      to && store.navigateTo(to)
    },
    [onActivate, onNavigate, store, to]
  )

  return (
    <li>
      <NavItem
        {...rest}
        href={path}
        active={isActive}
        onClick={handleOnClick}
      />
    </li>
  )
})

type NavButtonProps = NavCommonProps & {
  onClick: (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void
}
export function NavButtonLi(props: NavButtonProps) {
  return (
    <li>
      <NavItem {...props} active={false} />
    </li>
  )
}
